// covered-by: frontends/carrie/src/ask-v2/review/__tests__/useLoadedReviewCommentGroups.test.ts
import { useMemo, useRef } from "react";

import {
  type LoadedReviewCommentGroups,
  type ReviewCommentVisibilityFields,
  withLoadedReviewCommentGroups,
} from "../../utils/visibleReviewComments";

const NOTHING_LOADED: LoadedReviewCommentGroups = new Map();

/**
 * The section each finding sat in when the review loaded, held for the visit.
 *
 * why a snapshot and not `reviewCommentGroup` on every render: a delete, a
 * heart or a restore made in this session leaves the card in its place,
 * grayed or pressed, as #5850 built it; the next load files it under its new
 * section. The list's sections and the numbers the list and the pins share
 * both read this one snapshot, so a section stays contiguous under her hand.
 *
 * `loadKey` is the review the comments belong to: a new one (another version)
 * is a new load. With no key the snapshot lives as long as the caller.
 */
export function useLoadedReviewCommentGroups(
  comments:
    | readonly (ReviewCommentVisibilityFields & { _id: string })[]
    | undefined,
  loadKey?: string | null,
): LoadedReviewCommentGroups {
  const snapshot = useRef<{
    loadKey: string | null | undefined;
    groups: LoadedReviewCommentGroups;
  }>({ loadKey, groups: NOTHING_LOADED });
  return useMemo(() => {
    const kept =
      snapshot.current.loadKey === loadKey
        ? snapshot.current.groups
        : NOTHING_LOADED;
    const groups = withLoadedReviewCommentGroups(kept, comments ?? []);
    snapshot.current = { loadKey, groups };
    return groups;
  }, [comments, loadKey]);
}
